/**
 * jrs-v2-cart-submit.js — state.draft → ProductConfiguration adapter
 *                         + Doc 2 §10 sequential POST chain
 *
 * Phase 2 closeout (2026-05-18). The byte-locked Doc 2 contract gate.
 *
 * The popup's internal state.draft (Doc 6 §5.3 shape) does NOT match
 * the ProductConfiguration shape that the cart-payload-builder expects
 * (Doc 2 §2 worked examples). This module is the translator + submitter.
 *
 * Public API (JRS.CartSubmit):
 *   buildProductConfiguration(state, context) → ProductConfiguration
 *   submit(productConfiguration) → Promise<{ ok, addedItems, partialSuccess?, failedIndex? }>
 *   verifyAfterSubmit(productConfiguration, cart) → Promise<{ ok, missing? }>
 *
 * Dependencies:
 *   JRS.CartPayloadBuilder.build()    — LOCKED contract; this module's caller
 *   JRS.PositionCatalogue.getById()    — position → variantId + name mapping
 *   JRS.LogoService.readSavedLogos()   — resolve existing logoId → full descriptor
 *   JRS.CartAPI.addItems()             — actual cart POST
 *   JRS.CartAPI.getCart()              — for post-submit verification
 *   JRS.CartValidator.validateCart()   — heal pass after submits
 *
 * KNOWN LIMITATION (Phase 2.5 deferred; Phase 5):
 *   Bundle production cart-submit requires context.bundle_children[i].sizes
 *   to be populated. Phase 2.5's PDP trigger _readBundleChildren() returns
 *   null for sizes (the bundle's per-child size state lives in
 *   jrs-bundle-size-grid.js's dynamic state and isn't a static DOM
 *   selector). buildProductConfiguration() throws a clear error when
 *   given bundle context without sizes data; the bundle PDP cart-submit
 *   will surface this until the bundle reader is upgraded.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  // Doc 10 P1 — per-position variant-source map from the last buildProductConfiguration
  // call (positionName → 'blob_a' | 'shop_catalogue' | 'captured_reuse'). Read via
  // getLastVariantSources(). Sequential (one add at a time), so a module var is safe.
  var _lastVariantSources = {};

  // §2 ERP parity (2026-08-04) — production expects NUMERIC logo ids (control
  // mints String(Math.floor(Math.random()*999999)) per upload). Saved/re-used
  // logos can carry legacy non-numeric ids; normalise at ADD time with a
  // per-build re-mint so every consumer (_Logo Id N, _customization_data,
  // _bundle_raw, child _Logo Id) sees the same numeric id for one build. No
  // saved-store migration: the store keeps its original ids, the lookup below
  // runs BEFORE the remap.
  var _logoIdRemap = {};

  function _numericLogoId(id) {
    var key = String(id == null ? '' : id);
    if (/^\d+$/.test(key)) return key;
    if (!_logoIdRemap[key]) {
      _logoIdRemap[key] = (JRS.LogoService && JRS.LogoService.mintLogoId)
        ? JRS.LogoService.mintLogoId()
        : String(Math.floor(Math.random() * 999999));
    }
    return _logoIdRemap[key];
  }

  // -------------------------------------------------------------------
  // ProductConfiguration construction
  // -------------------------------------------------------------------

  /**
   * Translate the popup's state.draft + PDP context into the byte-locked
   * ProductConfiguration shape that JRS.CartPayloadBuilder.build() accepts.
   *
   * @param {Object} state - JRS.CustomizerPopupState snapshot
   * @param {Object} context - PDP context (window.JRS_PDP_CONTEXT shape)
   * @returns {Object} ProductConfiguration per Doc 2 §2
   */
  function buildProductConfiguration(state, context) {
    // Doc 10 P1 — variant-source side-channel. The two customization builders
    // record each position's resolution source here (positionName → 'blob_a' |
    // 'shop_catalogue' | 'captured_reuse') as they resolve. Kept OFF the returned
    // ProductConfiguration so the byte-locked §2 fixtures + the bundle-heal
    // reconstruction stay untouched; the confirm handler reads it via
    // getLastVariantSources() and folds it into the parent's _jrs_provenance.
    _lastVariantSources = {};
    _logoIdRemap = {};
    var draft = (state && state.draft) || {};
    var ctx = context || {};
    var stateFlow = (state && state.flow) || 'multistep';

    // Bundle dispatch — needs to recognise three entry points:
    //   1. PDP bundle cart-add: state.flow === 'bundle' (set by
    //      JRS.FlowBundle.start). The original / canonical path.
    //   2. Bundle cart-edit: state.flow === 'cart-edit' (OPEN_FOR_
    //      CART_EDIT reducer always sets this regardless of underlying
    //      source flow). The bundle-vs-multistep distinction lives on
    //      the draft, not state.flow. Two redundant signals stamped by
    //      openFromCartLine post-parse:
    //        - draft.flow === 'bundle' (parser-flow decoration)
    //        - draft._bundle_meta present (parser auxiliary output)
    //      Either signal routes to bundle.
    //   3. Multistep cart-edit: state.flow === 'cart-edit' AND draft
    //      has neither bundle signal → falls through to multistep.
    //
    // Phase 4 v2 Session 3 commit 1 (2026-05-22) — added cases 2 + 3.
    // Without this fix, bundle cart-edit save would route through the
    // multistep adapter and produce a wrong-shape ProductConfiguration
    // (single bundle-variant size entry instead of N sub-product
    // categories), corrupting the customer's cart on save.
    var isBundle = (stateFlow === 'bundle') ||
                   (draft.flow === 'bundle') ||
                   !!draft._bundle_meta;

    if (isBundle) {
      return _buildBundleConfiguration(draft, ctx);
    }
    return _buildMultistepConfiguration(draft, ctx);
  }

  function _buildMultistepConfiguration(draft, ctx) {
    var sizes = _normaliseSizes(ctx.sizes);
    if (sizes.length === 0) {
      // Single-size product without quantity inputs (e.g. legacy PDP):
      // synthesise a single-size entry from the product's default variant
      // so the contract still validates.
      sizes = [{
        size: 'OS',
        quantity: 1,
        variantId: ctx.productVariantId || ctx.productId || 0,
        sku: ''
      }];
    }

    return {
      type: 'standard',
      instanceId: _mintInstanceId(),
      timestamp: _mintTimestamp(),
      categories: [
        {
          name: ctx.productTitle || 'Product',
          index: 0,
          products: [
            {
              productId: ctx.productId || 0,
              productHandle: ctx.productHandle || '',
              productTitle: ctx.productTitle || '',
              variantId: sizes[0].variantId,
              colour: ctx.productColour || '',
              imageUrl: ctx.productImage || '',
              sizes: sizes
            }
          ]
        }
      ],
      customization: _buildCustomization(draft, /* defaultCategoryIndex */ 0)
    };
  }

  function _buildBundleConfiguration(draft, ctx) {
    if (!ctx.bundle_children || ctx.bundle_children.length === 0) {
      throw new Error(
        'CartSubmit: bundle flow requires context.bundle_children. ' +
        'The PDP trigger\'s _readBundleChildren() did not populate this — ' +
        'either the bundle PDP is missing [data-jrs-product-card] elements, ' +
        'or the trigger fired before the bundle build was complete.'
      );
    }

    var hasAnyMissingSizes = false;
    for (var i = 0; i < ctx.bundle_children.length; i++) {
      if (!ctx.bundle_children[i].sizes || ctx.bundle_children[i].sizes.length === 0) {
        hasAnyMissingSizes = true;
        break;
      }
    }
    if (hasAnyMissingSizes) {
      // Defensive guard, not an unimplemented feature. The PDP trigger's
      // _readBundleChildren() DOES extract per-child size/qty/variantId/sku
      // from the jrs-bundle-size-grid.js `.mrk-qty-item` inputs and skips any
      // card with no quantities, so a normally-configured bundle always
      // arrives with populated sizes (adapter tests cover the §2.3/§2.4 sized
      // fixtures). This throw only fires if a child reaches the adapter with
      // sizes null/empty — i.e. the size grid never rendered (no colour
      // selected) or context was populated by some other path. Fail loud
      // rather than POST a bundle with a missing garment.
      throw new Error(
        'CartSubmit: bundle children missing per-size data. ' +
        'context.bundle_children[].sizes is null or empty — the bundle size ' +
        'grid had no entered quantities when the trigger read it. Select ' +
        'sizes on every configured garment before confirming.'
      );
    }

    // Per-product (Phase per_product S3): when branding_strategy is
    // 'per_product' AND the S1 category_configs accumulator is populated, the
    // per-category branding lives there (not the flat draft). Build categories
    // grouped per metaobject category (Blob A names) and a customization that
    // scopes each position to its category. The legacy per_product
    // representation (flat positions + position_categories, no category_configs
    // — the §2.4 fixture) and bundle_wide both fall through to _buildCustomization
    // unchanged.
    var isPerProduct = draft.branding_strategy === 'per_product' &&
                       _objectKeyCount(draft.category_configs) > 0;

    var categories = isPerProduct
      ? _buildPerProductBundleCategories(draft, ctx)
      : _buildBundleCategories(ctx.bundle_children, ctx.bundle_categories);

    return {
      type: 'bundle',
      instanceId: _mintInstanceId(),
      timestamp: _mintTimestamp(),
      bundleMetaobjectId: ctx.bundleMetaobjectId || '',
      bundleProductHandle: ctx.bundleProductHandle || '',
      bundleProductVariantId: ctx.bundleProductVariantId || 0,
      categories: categories,
      customization: isPerProduct
        ? _buildCustomizationPerProduct(draft, ctx)
        : _buildCustomization(
            draft,
            /* defaultCategoryIndex */ 0,
            /* useBundleWide */ draft.branding_strategy === 'bundle_wide',
            /* blobCats */ ctx.bundle_categories,
            /* bundleChildren */ ctx.bundle_children
          )
    };
  }

  function _buildBundleCategories(children, blobCats) {
    // §4 ERP parity (2026-08-04): category name must be the metaobject
    // category LABEL (control: "T-Shirts"), not the selected product's title.
    // _readBundleChildren carries no categoryName, so resolve each child's
    // handle against Blob A's productHandles. Product-title fallback remains
    // for carts/PDPs where bundle_categories isn't populated.
    var handleToCategory = {};
    var cats = blobCats || [];
    for (var ci = 0; ci < cats.length; ci++) {
      var handles = (cats[ci] && cats[ci].productHandles) || [];
      for (var hi = 0; hi < handles.length; hi++) {
        if (cats[ci].name) handleToCategory[String(handles[hi])] = cats[ci].name;
      }
    }
    var out = [];
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      out.push({
        name: child.categoryName ||
              handleToCategory[String(child.productHandle || '')] ||
              child.productTitle || ('Item ' + (i + 1)),
        index: i,
        products: [
          {
            productId: child.productId || 0,
            productHandle: child.productHandle || '',
            productTitle: child.productTitle || '',
            variantId: (child.sizes && child.sizes[0] && child.sizes[0].variantId) || 0,
            colour: child.colour || '',
            imageUrl: child.imageUrl || '',
            sizes: _normaliseSizes(child.sizes)
          }
        ]
      });
    }
    return out;
  }

  /**
   * Build the `customization.positions[]` array from state.draft.
   *
   * @param {Object} draft - state.draft
   * @param {number} defaultCategoryIndex
   * @param {boolean} [useBundleWide=false] - when true, every position
   *           emits sourceLogoGroupId='bundlewide-1' and categoryIndex=0
   *           (Doc 2 §6 Step 0A — single logo across all categories).
   *           When false (multistep OR bundle per-product), positions
   *           use state.draft.position_categories[pid] when present
   *           (Phase 3 will populate this for per-product bundles)
   *           else fall back to defaultCategoryIndex.
   * @returns {Object|null} { positions: [...] } or null if no positions
   */
  /**
   * Safety guard (method restrictions). Throws when a chosen application
   * method isn't allowed for its position under the current product tags +
   * bundle slot. The step controllers already hide disallowed methods, so
   * this only trips on a stale or tampered draft; throwing routes to the
   * confirm handler's `build_failed` telemetry instead of silently sending a
   * wrong variant to Shopify. No-op when JRS.MethodRestrictions is absent or
   * the method is unset (a different validation owns the unset case).
   *
   * @param {string} positionId
   * @param {string} method - lowercase 'embroidery' | 'print'
   * @param {number|null} slot - 1-based bundle slot (per_product) or null
   * @param {Object|null} descriptor - catalogue descriptor (variant ids)
   */
  /**
   * Clamp an application method to one the position actually ALLOWS, instead of
   * throwing (which blocked add-to-cart / cart-edit save). A reopened cart line, or
   * the catalogue default (large placements default to print), can carry a method
   * the restrictions forbid — e.g. `print` on a slot-3 / hide-print Large Back. That
   * must NEVER block the purchase: we substitute the allowed method (embroidery here)
   * and warn. Returns the method unchanged when it's already allowed, else the first
   * allowed method (JRS.MethodRestrictions.getAllowedMethods is never empty). Lowercase
   * in/out — callers `_capitalise()` for the payload. Same tag/slot source the former
   * `_assertMethodAllowed` used (JRS_PDP_CONTEXT.productTags + the bundle slot).
   *
   * @param {string} positionId
   * @param {string} method - 'embroidery' | 'print' (lowercase)
   * @param {number|null} slot - 1-based bundle slot (per_product) or null
   * @param {Object|null} descriptor - catalogue descriptor (variant ids)
   * @returns {string} an allowed method
   */
  function _clampMethodToAllowed(positionId, method, slot, descriptor, isBundleFlow) {
    if (!method) return method;
    if (!JRS.MethodRestrictions || typeof JRS.MethodRestrictions.getAllowedMethods !== 'function') return method;
    var lower = String(method).toLowerCase();
    var tags = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                window.JRS_PDP_CONTEXT.productTags) || [];
    // Launch-blocker fix (2026-08-04, ERP round 3 §A): on BUNDLE flows the
    // parent product's hide-* / remove-{position}-{method} tags must NOT feed
    // this clamp. Control's bundle steps ignore those tags entirely (they are
    // a single-product-PDP mechanism scoped to the GARMENT's own tags), and
    // the v3 method-step UI builds its options from child/catalogue scope —
    // so applying parent tags here silently converted the customer's chosen
    // method (seen live: Print chosen as the only rendered option, order
    // recorded Embroidery at the embroidery price). Bundle clamps keep only
    // the mix&match slot rules (parent-scope by design) + variant presence,
    // guaranteeing the UI's offered set is a subset of the allowed set.
    var allowed = JRS.MethodRestrictions.getAllowedMethods({
      positionId: positionId,
      productTags: isBundleFlow ? [] : tags,
      mixAndMatch: isBundleFlow
        ? (typeof JRS.MethodRestrictions.isMixAndMatchBundle === 'function' &&
           JRS.MethodRestrictions.isMixAndMatchBundle(tags))
        : undefined,
      bundleSlot: (typeof slot === 'number') ? slot : null,
      embroideryVariantId: descriptor ? descriptor.embroideryVariantId : undefined,
      printVariantId: descriptor ? descriptor.printVariantId : undefined
    });
    if (allowed.indexOf(lower) !== -1) return method; // already allowed — unchanged
    var clamped = allowed[0]; // never empty
    if (window.console && typeof console.warn === 'function') {
      console.warn(
        'CartSubmit: application method "' + method + '" is not allowed for position "' +
        positionId + '"' + ((typeof slot === 'number') ? ' (bundle slot ' + slot + ')' : '') +
        ' (hide-* tag / mix-and-match slot rule); clamping to "' + clamped + '" instead of blocking.'
      );
    }
    return clamped;
  }

  function _buildCustomization(draft, defaultCategoryIndex, useBundleWide, blobCats, bundleChildren) {
    var positions = draft.positions || [];
    if (positions.length === 0) return null;

    var methodMap = draft.application_methods || {};
    var assignments = draft.logo_assignments || {};
    var positionCategories = draft.position_categories || {};
    var textLogo = draft.text_logo || null;
    var savedLogos = _readSavedLogosCache();

    // D2d — the per-child method engine engages for a bundle_wide add only when
    // a child's OWN existing restriction (whole-product hide-embroidery /
    // hide-print — the same tags both PDPs already read, carried per child by
    // D1) would exclude the customer's desired method at a given position. The
    // decision is per-position (below). When no child excludes the desired
    // method, the original emission runs unchanged and is byte-identical to the
    // pre-engine path (this keeps the 4 Doc 2 fixtures + adapter tests green).
    var bundleWideChildren = (useBundleWide && bundleChildren && bundleChildren.length)
      ? bundleChildren : null;

    var out = [];
    for (var i = 0; i < positions.length; i++) {
      var positionId = positions[i];
      var descriptor = (JRS.PositionCatalogue && JRS.PositionCatalogue.getById(positionId)) || null;
      var assignment = assignments[positionId] || null;
      if (!descriptor || !assignment) {
        throw new Error(
          'CartSubmit: position "' + positionId + '" missing catalogue descriptor or logo assignment. ' +
          'Cannot build ProductConfiguration.'
        );
      }

      // Safety guard: the chosen method must still be allowed for this position
      // (hide-* / remove-* tag / mix-and-match slot rule). Runs for multistep +
      // per_product, which configure a single garment, so the PDP context's tags
      // are that garment's own.
      //
      // NOT for bundle_wide: there, the customer's choice is only a PREFERENCE
      // that is resolved PER GARMENT below (from each child's OWN tags, via
      // _emitBundleWidePerChild → resolveCategoryMethod, which never yields a
      // disallowed method). Asserting the preference against the bundle PARENT's
      // tags is both wrong (parent tags ≠ any child's) and redundant — and it
      // would falsely throw whenever the parent carries a restriction that the
      // individual garments don't (e.g. a stray tag on the bundle product).
      // Non-bundle_wide (multistep / per_product-single): clamp the position's
      // method to one the restrictions allow rather than blocking the add. bundle_wide
      // keeps the raw preference — the per-child engine resolves each garment below.
      var effectiveMethod = methodMap[positionId];
      if (!bundleWideChildren) {
        // §A (2026-08-04): bundle flows (incl. the legacy flat-per_product
        // representation) must not clamp against the bundle PARENT's tags —
        // only multistep configures the garment whose tags the PDP context holds.
        var _isBundleFlow2 = !!useBundleWide || !!(bundleChildren && bundleChildren.length);
        effectiveMethod = _clampMethodToAllowed(positionId, effectiveMethod, null, descriptor, _isBundleFlow2);
      }

      var logo = _resolveLogoConfiguration(assignment, textLogo, savedLogos);

      // D2d per-position gate: engage the per-child engine only when a child's
      // own restriction would exclude the desired method here. Otherwise fall
      // through to the original single-bundlewide emission (byte-identical).
      if (bundleWideChildren &&
          _anyChildExcludesDesired(bundleWideChildren, positionId, methodMap[positionId], descriptor)) {
        _emitBundleWidePerChild(out, {
          positionId: positionId, descriptor: descriptor, logo: logo,
          desired: methodMap[positionId], children: bundleWideChildren, blobCats: blobCats
        });
        continue;
      }

      var categoryIndex = useBundleWide
        ? 0
        : (typeof positionCategories[positionId] === 'number'
            ? positionCategories[positionId]
            : defaultCategoryIndex);

      // Money path: prefer the merchant's per-position variant from Blob A (the
      // bundle bootstrap — carries the FREE Left Breast variant, mirroring live's
      // position.pricing.value.variants[1]) over the shop-catalogue descriptor
      // (which on a bundle page resolves to the paid product-level position).
      // 0 when absent (multistep PDP: no bundle_categories) → catalogue fallback.
      var blobVariant = _resolveBundleWideVariantFromBlob(positionId, effectiveMethod, blobCats);
      // Doc 10 P1 — record the resolution source (free-logo tracer); off-config.
      _lastVariantSources[descriptor.name] = blobVariant ? 'blob_a' : 'shop_catalogue';

      out.push({
        id: _mintInstanceId(),
        positionName: descriptor.name,
        positionVariantId: blobVariant || descriptor.positionVariantId,
        applicationMethod: _capitalise(effectiveMethod),
        categoryIndex: categoryIndex,
        sourceLogoGroupId: useBundleWide ? 'bundlewide-1' : null,
        logo: logo
      });
    }

    return { positions: out };
  }

  // -------------------------------------------------------------------
  // D2b — bundle_wide per-child method engine
  // -------------------------------------------------------------------
  //
  // For a bundle_wide add, `_buildBundleCategories` builds ONE category per
  // child (index i == children[i]), so `categoryIndex` aligns 1:1 with the
  // child list. We resolve each child's effective method (D1
  // resolveCategoryMethod, one child per category) and gate on divergence:
  //   - all children resolve to the SAME method → emit the existing single
  //     `bundlewide-1` position (byte-identical to pre-D2b when that method is
  //     the desired one; a uniform FORCED method — every child tagged the same
  //     way — collapses here too, with the forced method).
  //   - methods DIVERGE → emit one position per child: sourceLogoGroupId=null,
  //     categoryIndex=i, the child's resolved method + that method's variant id.
  //     This is the per_product Step 0B shape the byte-locked builder already
  //     routes per-category (getApplicableCategoryIndices → [categoryIndex]),
  //     and which reopens as per_product (methods preserved) — see D2 report.

  // True when any child's OWN allowed set (its tags → getAllowedMethods)
  // EXCLUDES the desired method at this position — i.e. applying the customer's
  // single bundle-wide choice would emit a method that child can't do. This is
  // the D2d engagement gate; when false the original single-bundlewide emission
  // runs unchanged (byte-identical). Uses the SAME variant ids + base methods as
  // _emitBundleWidePerChild's resolveCategoryMethod, so the gate and the
  // emission agree exactly (a gate hit ⇔ at least one child is forced).
  function _anyChildExcludesDesired(children, positionId, desired, descriptor) {
    if (!children || !children.length) return false;
    if (!JRS.MethodRestrictions || typeof JRS.MethodRestrictions.getAllowedMethods !== 'function') return false;
    var variantIds = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getVariantIdsFor === 'function')
      ? JRS.PositionCatalogue.getVariantIdsFor(positionId)
      : { embroidery: 0, print: 0 };
    var baseMethods = descriptor && descriptor.application_methods;
    // Slot context (2026-08-01, control parity): in bundle_wide each child is
    // 1:1 with a category (child index == categoryIndex), so its bundle slot is
    // index + 1. mixAndMatch comes from the PARENT's tags (JRS_PDP_CONTEXT) —
    // children never carry the bundle/mix&match tags themselves.
    var parentMix = _parentIsMixAndMatch();
    for (var i = 0; i < children.length; i++) {
      var allowed = JRS.MethodRestrictions.getAllowedMethods({
        positionId: positionId,
        productTags: (children[i] && children[i].tags) || [],
        bundleSlot: i + 1,
        mixAndMatch: parentMix,
        embroideryVariantId: variantIds.embroidery,
        printVariantId: variantIds.print,
        baseMethods: baseMethods
      });
      if (allowed.indexOf(desired) === -1) return true;
    }
    return false;
  }

  // True when the bundle PARENT product is a mix&match bundle (its own tags,
  // read from the PDP context — the slot rules' engagement signal).
  function _parentIsMixAndMatch() {
    if (!JRS.MethodRestrictions || typeof JRS.MethodRestrictions.isMixAndMatchBundle !== 'function') return false;
    var tags = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                window.JRS_PDP_CONTEXT.productTags) || [];
    return JRS.MethodRestrictions.isMixAndMatchBundle(tags);
  }

  // Resolve the branding variant id for a specific method at a position:
  // Blob A per-method (bundle bootstrap, free-logo aware) → shop catalogue
  // per-method (getVariantIdsFor) → 0 (caller skips + diagnoses).
  function _variantForMethod(positionId, method, blobCats) {
    var blobVariant = _resolveBundleWideVariantFromBlob(positionId, method, blobCats);
    if (blobVariant > 0) return { id: blobVariant, source: 'blob_a' };
    var ids = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getVariantIdsFor === 'function')
      ? JRS.PositionCatalogue.getVariantIdsFor(positionId)
      : { embroidery: 0, print: 0 };
    var catVariant = (method === 'print') ? ids.print : ids.embroidery;
    if (catVariant > 0) return { id: catVariant, source: 'shop_catalogue' };
    return { id: 0, source: 'shop_catalogue' };
  }

  function _emitBundleWidePerChild(out, opts) {
    var positionId = opts.positionId;
    var descriptor = opts.descriptor;
    var desired = opts.desired;
    var children = opts.children || [];
    var blobCats = opts.blobCats;

    var variantIds = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getVariantIdsFor === 'function')
      ? JRS.PositionCatalogue.getVariantIdsFor(positionId)
      : { embroidery: 0, print: 0 };
    var baseMethods = descriptor.application_methods;

    // Resolve each child (== one category) independently, with its real slot
    // (index + 1) so the mix&match slot rules apply per garment exactly as the
    // control theme applies them per bundle step (2026-08-01 parity).
    var emitParentMix = _parentIsMixAndMatch();
    var perChild = [];
    var first = null;
    var divergent = false;
    for (var c = 0; c < children.length; c++) {
      var res = JRS.MethodRestrictions.resolveCategoryMethod(
        [children[c]], positionId, desired, variantIds, baseMethods,
        { bundleSlot: c + 1, mixAndMatch: emitParentMix });
      if (res.conflict && window.console && typeof console.error === 'function') {
        // Workstream D — intra-category conflict. Cannot happen in the 1:1
        // bundle_wide category model (one child per category), but flagged
        // loudly for QA/D3 if a future grouping ever produces it.
        console.error(
          'CartSubmit D2b: intra-category method conflict at position "' + positionId +
          '" for child "' + ((children[c] && children[c].productHandle) || ('#' + c)) +
          '" — no method works for every child in the category; falling back to "' +
          desired + '".'
        );
      }
      perChild.push(res.method);
      if (first === null) first = res.method;
      else if (res.method !== first) divergent = true;
    }

    if (!divergent) {
      // Uniform — one bundlewide position (byte-identical shape to pre-D2b).
      var v = _variantForMethod(positionId, first, blobCats);
      _lastVariantSources[descriptor.name] = v.source;
      out.push({
        id: _mintInstanceId(),
        positionName: descriptor.name,
        positionVariantId: v.id || descriptor.positionVariantId,
        applicationMethod: _capitalise(first),
        categoryIndex: 0,
        sourceLogoGroupId: 'bundlewide-1',
        logo: opts.logo
      });
      return;
    }

    // Divergent — one position per child/category (per_product Step 0B shape).
    for (var k = 0; k < children.length; k++) {
      var method = perChild[k];
      var mv = _variantForMethod(positionId, method, blobCats);
      if (!mv.id) {
        // Catalogue gap: no variant for the resolved method (ties to the
        // unmatched-logo-positions item). Skip rather than emit an
        // unresolvable position that would fail cart-add.
        if (window.console && typeof console.error === 'function') {
          console.error(
            'CartSubmit D2b: no variant id for position "' + positionId + '" method "' +
            method + '" (category ' + k + ') — skipping this category. Check the ' +
            'position metaobject\'s embroidery/print variant references.'
          );
        }
        continue;
      }
      _lastVariantSources[descriptor.name + ' [cat ' + k + ']'] = mv.source;
      out.push({
        id: _mintInstanceId(),
        positionName: descriptor.name,
        positionVariantId: mv.id,
        applicationMethod: _capitalise(method),
        categoryIndex: k,
        sourceLogoGroupId: null,
        logo: opts.logo
      });
    }
  }

  // -------------------------------------------------------------------
  // Per-product bundle (Phase per_product S3, spec D9 + resolved decisions)
  // -------------------------------------------------------------------
  //
  // Forward per_product cart-submit reads draft.category_configs (the S1
  // accumulator) instead of the flat draft. Each configured category emits its
  // own positions, scoped to that category by categoryIndex + a null (non-
  // bundlewide) sourceLogoGroupId — the exact shape the byte-locked builder's
  // Step 0B worked example uses (getApplicableCategoryIndices returns
  // [pos.categoryIndex] for non-bundlewide positions; bundlewide-* applies to
  // every category).
  //
  // Two design decisions resolved with Hamza (2026-06-04):
  //   * positionVariantId is RE-DERIVED from Blob A (ctx.bundle_categories[K]
  //     .positions, method-specific embroidery/print variant) because
  //     category_configs persists only position-id strings — not the per-
  //     category variant ids the S2 position_picker used. Falls back to the
  //     shop catalogue when Blob A has no match.
  //   * Contiguous categories only. The byte-locked builder requires a dense
  //     categoryIndex (it array-accesses categories[idx] and asserts
  //     index === array position). For contiguous bundles dense == Blob A
  //     index, so categoryIndex = the dense ordinal is exact. Sparse/skip-empty
  //     (a filtered-out middle category → keys like 0,2) compresses to dense
  //     ordinals with a console warning — a documented Phase 5 limitation.

  function _handleizePosition(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function _objectKeyCount(obj) {
    if (!obj || typeof obj !== 'object') return 0;
    var n = 0;
    for (var k in obj) { if (Object.prototype.hasOwnProperty.call(obj, k)) n++; }
    return n;
  }

  // Numeric-sorted category_configs keys (the real metaobject indices).
  function _sortedPerProductKeys(configs) {
    var keys = [];
    for (var k in configs) {
      if (Object.prototype.hasOwnProperty.call(configs, k)) {
        var n = parseInt(k, 10);
        if (!isNaN(n) && n >= 0) keys.push(n);
      }
    }
    keys.sort(function (a, b) { return a - b; });
    return keys;
  }

  function _findBlobCategory(blobCats, realKey) {
    for (var i = 0; i < blobCats.length; i++) {
      if (blobCats[i] && blobCats[i].categoryIndex === realKey) return blobCats[i];
    }
    return null;
  }

  // Children whose productHandle belongs to the category's Blob A handle list.
  function _childrenForHandles(children, handles) {
    if (!handles || !handles.length) return [];
    var set = {};
    for (var h = 0; h < handles.length; h++) set[handles[h]] = true;
    var out = [];
    for (var c = 0; c < children.length; c++) {
      if (children[c] && set[children[c].productHandle]) out.push(children[c]);
    }
    return out;
  }

  function _childToBundleProduct(child) {
    return {
      productId: child.productId || 0,
      productHandle: child.productHandle || '',
      productTitle: child.productTitle || '',
      variantId: (child.sizes && child.sizes[0] && child.sizes[0].variantId) || 0,
      colour: child.colour || '',
      imageUrl: child.imageUrl || '',
      sizes: _normaliseSizes(child.sizes)
    };
  }

  // categories[] for per_product: one entry per configured category, dense
  // ordinal index (== array position, the builder invariant), named from Blob A
  // (the metaobject category name — _readBundleChildren carries no categoryName),
  // grouping the children whose handles belong to it.
  function _buildPerProductBundleCategories(draft, ctx) {
    var configs = draft.category_configs || {};
    var keys = _sortedPerProductKeys(configs);
    var blobCats = (ctx && ctx.bundle_categories) || [];
    var children = (ctx && ctx.bundle_children) || [];
    var out = [];
    for (var ki = 0; ki < keys.length; ki++) {
      var realKey = keys[ki];
      var blobCat = _findBlobCategory(blobCats, realKey);
      var handles = (blobCat && blobCat.productHandles) || [];
      var matched = _childrenForHandles(children, handles);
      if (matched.length === 0 && children[ki]) matched = [children[ki]]; // positional fallback
      var products = [];
      for (var m = 0; m < matched.length; m++) products.push(_childToBundleProduct(matched[m]));
      // Category name → _BundleCategoryName / _BundleItemName (Doc 2 §6.1).
      // Prefer the metaobject category name; when bundle_categories isn't
      // populated (the usual case) fall back to the selected product's real
      // title so production sees a meaningful name, not the generic "Product N".
      var name = (blobCat && blobCat.name) ||
                 (matched[0] && matched[0].productTitle) ||
                 ('Product ' + (realKey + 1));
      out.push({ name: name, index: ki, products: products });
    }
    return out;
  }

  // Method-specific branding SKU re-derived from Blob A; falls back to the shop
  // catalogue's positionVariantId, then 0 (builder validation rejects 0).
  function _resolvePerProductVariantId(positionId, method, blobCat, descriptor) {
    if (blobCat && blobCat.positions) {
      for (var i = 0; i < blobCat.positions.length; i++) {
        var bp = blobCat.positions[i];
        if (bp && _handleizePosition(bp.name) === positionId) {
          var emb = (typeof bp.embroideryVariantId === 'number') ? bp.embroideryVariantId : 0;
          var prn = (typeof bp.printVariantId === 'number') ? bp.printVariantId : 0;
          var chosen = (method === 'print') ? prn : emb;
          if (chosen > 0) return chosen;
          if (emb > 0) return emb;
          if (prn > 0) return prn;
          break;
        }
      }
    }
    return (descriptor && descriptor.positionVariantId) || 0;
  }

  // Bundle_wide / legacy-flat position-variant re-derived from Blob A (the
  // [data-jrs-bundle-data] bootstrap, read into ctx.bundle_categories at click
  // time). The bootstrap carries the merchant's per-position pricing product —
  // including the FREE Left Breast embroidery variant — mirroring exactly what
  // the live theme reads (position.pricing.value.variants[1]). A bundle_wide logo
  // applies to every category and the position's variant is identical across them,
  // so we return the first category whose positions include a handle match.
  // Returns 0 when the bootstrap has no match (multistep PDP: no bundle_categories)
  // so the caller falls back to the shop-catalogue descriptor — WITHOUT this,
  // _buildCustomization read the shop catalogue directly and charged the paid
  // variant even when the merchant configured a free position for the bundle.
  function _resolveBundleWideVariantFromBlob(positionId, method, blobCats) {
    if (!blobCats || !blobCats.length) return 0;
    for (var c = 0; c < blobCats.length; c++) {
      var v = _resolvePerProductVariantId(positionId, method, blobCats[c], null);
      if (v > 0) return v;
    }
    return 0;
  }

  function _blobPositionName(blobCat, positionId) {
    if (!blobCat || !blobCat.positions) return '';
    for (var i = 0; i < blobCat.positions.length; i++) {
      var bp = blobCat.positions[i];
      if (bp && _handleizePosition(bp.name) === positionId) return bp.name;
    }
    return '';
  }

  // 'beanie_front' → 'Beanie Front'. Last-resort display name so a raw handle is
  // never stored in a cart line / order Position property.
  function _humanizePositionId(pid) {
    return String(pid || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  // Best available display name for a position: the catalogue name, else the
  // Blob A name, else a humanised id. A value that equals the id or still looks
  // like a handle (contains "_") is treated as not-a-real-name.
  function _prettyPositionName(descriptorName, blobName, positionId) {
    if (descriptorName && String(descriptorName) !== String(positionId) &&
        String(descriptorName).indexOf('_') === -1) return String(descriptorName);
    if (blobName && String(blobName) !== String(positionId) &&
        String(blobName).indexOf('_') === -1) return String(blobName);
    return _humanizePositionId(positionId);
  }

  // Build customization.positions[] from draft.category_configs (per_product).
  // Iterates the same sorted keys as _buildPerProductBundleCategories so the
  // dense categoryIndex aligns with the categories[] array position.
  function _buildCustomizationPerProduct(draft, ctx) {
    var configs = draft.category_configs || {};
    var keys = _sortedPerProductKeys(configs);
    if (keys.length === 0) return null;

    // Contiguous-only guard (resolved decision): warn on sparse keys; the
    // dense-ordinal categoryIndex below still keeps the builder from throwing,
    // but _BundleCategoryIndex compresses (Phase 5).
    var contiguous = true;
    for (var ci = 0; ci < keys.length; ci++) { if (keys[ci] !== ci) { contiguous = false; break; } }
    if (!contiguous && window.console && console.warn) {
      console.warn(
        'CartSubmit: per_product category_configs keys are non-contiguous (' + keys.join(',') +
        '). S3 supports contiguous categories only — _BundleCategoryIndex is compressed to dense ' +
        'ordinals for this bundle (Phase 5 limitation).'
      );
    }

    var blobCats = (ctx && ctx.bundle_categories) || [];
    var savedLogos = _readSavedLogosCache();
    var out = [];

    for (var ki = 0; ki < keys.length; ki++) {
      var realKey = keys[ki];
      var cfg = configs[realKey] || {};
      var positions = cfg.positions || [];
      var methodMap = cfg.application_methods || {};
      var assignments = cfg.logo_assignments || {};
      var textLogo = cfg.text_logo || null;
      var logoVariants = cfg.logo_variants || {};
      var blobCat = _findBlobCategory(blobCats, realKey);

      for (var pi = 0; pi < positions.length; pi++) {
        var positionId = positions[pi];
        var assignment = assignments[positionId] || null;
        if (!assignment) {
          throw new Error(
            'CartSubmit: per_product category ' + realKey + ' position "' + positionId +
            '" has no logo assignment. Cannot build ProductConfiguration.'
          );
        }
        var descriptor = (JRS.PositionCatalogue && JRS.PositionCatalogue.getById(positionId)) || null;
        // per_product has a real bundle slot (realKey + 1), so mix-and-match slot
        // lock-outs apply alongside hide-* tag rules. Clamp a disallowed stored/default
        // method (e.g. print on a slot-3 / hide-print Large Back) to the allowed one
        // instead of throwing — a cart-edit save must never be blocked by this.
        var method = _clampMethodToAllowed(positionId, methodMap[positionId] || 'embroidery', realKey + 1, descriptor, /* isBundleFlow */ true);
        // Faithful round-trip: on a cart-edit reopen the parser captured the
        // original `_logoVariant N` (paired with its method) into cfg.logo_variants.
        // Reuse that exact position variant when the method is UNCHANGED, so an
        // unedited position re-saves byte-identically instead of re-deriving from
        // the (null-in-prod) bundle metaobject / catalogue. When the customer edited
        // the method — or on the forward add path (no logo_variants) — re-derive.
        var lv = logoVariants[positionId];
        var _reuse = !!(lv && Number(lv.variantId) > 0 && lv.method === method);
        var variantId = _reuse
          ? Number(lv.variantId)
          : _resolvePerProductVariantId(positionId, method, blobCat, descriptor);
        // Prefer a real display name (catalogue → Blob A); never store a raw
        // handle like "beanie_front" in the cart line / order — humanise the id
        // as a last resort ("beanie_front" → "Beanie Front"), matching what the
        // live theme's Position property carries. (Beanie/cap positions live in
        // Blob A, not the shop catalogue, so descriptor is often null here.)
        var positionName = _prettyPositionName((descriptor && descriptor.name),
                             _blobPositionName(blobCat, positionId), positionId);
        // Doc 10 P1 — resolution source (free-logo tracer); off-config. captured_reuse
        // (cart-edit faithful round-trip) vs Blob A (merchant/free) vs shop catalogue.
        _lastVariantSources[positionName] = _reuse
          ? 'captured_reuse'
          : (_resolvePerProductVariantId(positionId, method, blobCat, null) > 0 ? 'blob_a' : 'shop_catalogue');
        var logo = _resolveLogoConfiguration(assignment, textLogo, savedLogos);

        out.push({
          id: _mintInstanceId(),
          positionName: positionName,
          positionVariantId: variantId,
          applicationMethod: _capitalise(method),
          categoryIndex: ki, // dense ordinal (== Blob A index for contiguous)
          sourceLogoGroupId: null, // non-bundlewide → builder scopes to [categoryIndex]
          logo: logo
        });
      }
    }

    return out.length ? { positions: out } : null;
  }

  /**
   * Resolve a state.draft.logo_assignments[positionId] entry into the
   * full LogoConfiguration shape Doc 2 §2 expects.
   *
   * Three source variants:
   *   - 'upload': assignment carries url + filename inline (commit b8e6565
   *     wired saveLogo to populate these before state dispatch)
   *   - 'existing': assignment carries logoId only; lookup against the
   *     saved-logos cache for full url/filename/type
   *   - 'text': assignment carries the text-logo's id; the actual text
   *     payload lives in state.draft.text_logo (singular — Phase 2.5
   *     limit is one text logo per popup session)
   */
  function _resolveLogoConfiguration(assignment, textLogo, savedLogos) {
    if (!assignment) return null;

    var source = assignment.source;

    if (source === 'text') {
      var lines = (textLogo && textLogo.lines) ? textLogo.lines.slice() : ['', '', '', ''];
      while (lines.length < 4) lines.push('');
      return {
        type: 'text',
        logoId: _numericLogoId(assignment.logoId),
        url: null,
        filename: null,
        text: {
          lines: lines,
          colour: (textLogo && textLogo.color) || '#000000',
          font: (textLogo && textLogo.font) || 'Standard'
        },
        note: (assignment && assignment.note) || ''
      };
    }

    if (source === 'upload') {
      return {
        type: 'upload',
        logoId: _numericLogoId(assignment.logoId),
        url: assignment.url || '',
        filename: assignment.filename || '',
        text: null,
        note: (assignment && assignment.note) || ''
      };
    }

    // 'existing' — look up the full descriptor from the saved-logos cache
    if (source === 'existing') {
      var saved = _findSavedLogo(savedLogos, assignment.logoId);
      if (!saved) {
        throw new Error(
          'CartSubmit: existing logoId "' + assignment.logoId + '" not found in saved logos. ' +
          'JRS.LogoService.readSavedLogos() returned no match.'
        );
      }
      if (saved.type === 'text') {
        // Existing text logo — reconstruct text payload from stored lines.
        var savedLines = (saved.text_lines || []).slice();
        while (savedLines.length < 4) savedLines.push('');
        return {
          type: 'text',
          logoId: _numericLogoId(saved.id),
          url: null,
          filename: null,
          text: {
            lines: savedLines,
            colour: saved.text_color || '#000000',
            font: saved.text_font || 'Standard'
          },
          note: (assignment && assignment.note) || ''
        };
      }
      return {
        type: 'upload',
        logoId: _numericLogoId(saved.id),
        url: saved.url || '',
        filename: saved.filename || '',
        text: null,
        note: (assignment && assignment.note) || ''
      };
    }

    throw new Error('CartSubmit: unknown logo source "' + source + '"');
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  function _normaliseSizes(rawSizes) {
    if (!rawSizes || rawSizes.length === 0) return [];
    var out = [];
    for (var i = 0; i < rawSizes.length; i++) {
      var s = rawSizes[i];
      var entry = {
        size: s.size || s.sizeLabel || '',
        quantity: s.quantity || 0,
        variantId: s.variantId || 0,
        sku: s.sku || ''
      };
      // Per-size colour — added ONLY when present (captured per (colour, size)
      // variant by the PDP trigger's _readBundleChildren). A category can mix
      // colours across sizes (e.g. Black XS + Bottle Green S/M), so the builder
      // stamps _ProductColor from this when set. Omitting it when absent keeps
      // the SizeEntry shape byte-identical for single-colour categories and the
      // byte-locked §2 ProductConfiguration fixtures (which carry no per-size
      // colour) — those fall back to the product's representative colour.
      if (s.colour) { entry.colour = s.colour; }
      out.push(entry);
    }
    return out;
  }

  function _capitalise(s) {
    if (typeof s !== 'string' || s.length === 0) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }

  function _mintInstanceId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function _mintTimestamp() {
    return new Date().toISOString();
  }

  function _readSavedLogosCache() {
    if (!JRS.LogoService || typeof JRS.LogoService.readSavedLogos !== 'function') {
      return [];
    }
    try {
      var result = JRS.LogoService.readSavedLogos();
      return (result && result.logos) ? result.logos : [];
    } catch (_e) {
      return [];
    }
  }

  function _findSavedLogo(logos, id) {
    if (!logos || !id) return null;
    for (var i = 0; i < logos.length; i++) {
      if (logos[i] && logos[i].id === id) return logos[i];
    }
    return null;
  }

  // -------------------------------------------------------------------
  // Submit chain — Doc 2 §10 cart submission patterns
  // -------------------------------------------------------------------

  /**
   * Submit a ProductConfiguration to the cart via the sequential POST
   * pattern per Doc 2 §10.2.
   *
   * Pattern decision (read from cart-payload-builder output, not from
   * the configuration directly — the builder is the contract owner):
   *   - Multistep single-size: 1 batch POST with parent + children
   *     (cart_submit_pattern: 'single_atc')
   *   - Multistep multi-size: 1 POST per parent variant, each carrying
   *     its parent + linked children
   *     (cart_submit_pattern: 'sequential_per_size')
   *   - Bundle: first POST = [parent, first_child]; subsequent POSTs =
   *     [single_child] each. Same-variant collisions on children are
   *     normal under per-size split (Doc 2 §10.2 + §6.3)
   *     (cart_submit_pattern: 'bundle_sequential')
   *
   * Mid-sequence failure: returns ok:false with partialSuccess flag,
   * addedItems collected so far, failedIndex of the failing POST.
   *
   * @param {Object} productConfiguration
   * @returns {Promise<{ok, addedItems, partialSuccess?, failedIndex?, error?}>}
   */
  function submit(productConfiguration) {
    if (!JRS.CartPayloadBuilder || typeof JRS.CartPayloadBuilder.build !== 'function') {
      return Promise.reject(new Error('CartSubmit.submit: JRS.CartPayloadBuilder unavailable'));
    }
    if (!JRS.CartAPI || typeof JRS.CartAPI.addItems !== 'function') {
      return Promise.reject(new Error('CartSubmit.submit: JRS.CartAPI unavailable'));
    }

    var cartPayload;
    try {
      cartPayload = JRS.CartPayloadBuilder.build(productConfiguration);
    } catch (err) {
      return Promise.reject(err);
    }

    var items = (cartPayload && cartPayload.items) || [];
    if (items.length === 0) {
      return Promise.resolve({
        ok: true,
        addedItems: [],
        pattern: 'noop',
        expectedPayload: cartPayload
      });
    }

    var groups = _groupItemsForSubmit(items, productConfiguration.type);
    var pattern = _describePattern(productConfiguration.type, groups);

    // B1 (run-2 relaunch fix) — display-order transform. Measured on this store
    // (2026-07-31, /cart.js + checkout captures): the cart and the checkout
    // order summary list items NEWEST-ADDED FIRST, and within one /cart/add
    // batch the items land in array order (later array entries are "newer").
    // The builder emits parent-before-children, so posting groups as built put
    // the LOGO line on top of the garment at checkout (run-1: 90/95 variant
    // carts). Reversing item order within each group AND the group posting
    // order makes the displayed cart exactly match builder order: each garment
    // parent directly above its own logo children, group 1 (first size /
    // bundle parent) on top. Grouping, payload properties and verification
    // keys are untouched — this reorders POSTs only.
    groups = _orderGroupsForDisplay(groups);

    // Carry the actual POSTed payload through to the caller so
    // submitAndVerify can use it for verification instead of re-building.
    // The builder mints a fresh _bundle_instance_id on every build()
    // call (cart-payload-builder.js:241), so verifying against a
    // re-built payload would never match the parent that was POSTed
    // here. The bundle child matcher uses _lineItemKey/_ProductVariantId/
    // _ProductSize and would still work, but the parent matcher checks
    // _bundle_instance_id (cart-submit.js:647) — that's why bundle
    // verification was reporting the parent as missing.
    _lastGroupResults = []; _splitsSoFar = 0;   // v8.1.2 / v8.2.0 — per submit()
    return _postSequentially(groups, [], 0, pattern).then(function (result) {
      result.expectedPayload = cartPayload;
      return result;
    });
  }

  /**
   * Group cart-payload items into per-POST batches per Doc 2 §10.2.
   *
   * @param {Array} items - flat items[] from CartPayloadBuilder.build
   * @param {string} type - 'standard' | 'bundle'
   * @returns {Array<Array>} array of item groups, each becoming one POST
   */
  function _groupItemsForSubmit(items, type) {
    if (type === 'bundle') {
      // Bundle: parent + every child in ONE POST (v8.2.0); only a byte-identical
      // duplicate entry is deferred to its own POST (Doc 2 §10.2 v0.10).
      var parent = null;
      var children = [];
      for (var i = 0; i < items.length; i++) {
        var props = items[i].properties || {};
        if (props._ParentVariant === undefined && props._bundle !== undefined) {
          parent = items[i];
        } else if (props._ParentVariant !== undefined) {
          children.push(items[i]);
        }
      }
      if (!parent) {
        // Defensive: if we can't identify the bundle parent, fall back
        // to a single batch POST and let Shopify decide.
        return [items];
      }
      if (children.length === 0) {
        return [[parent]];
      }
      // v8.2.0 — ONE /cart/add.js per bundle. Live test on the store (2026-09-07,
      // 11 / 25 / 49 / 81 lines in one call): Shopify keeps same-variant entries
      // as SEPARATE cart lines inside one `items` array whenever their properties
      // differ — and per-size children always differ (_ProductVariantId,
      // _ProductSize, _ProductSku, Applied to). Only a byte-identical entry (same
      // id, same properties) merges (live: two identical entries → one line, qty
      // summed). So only exact duplicates are deferred to their own POST; the
      // builder never emits one, so in practice a bundle is a single POST.
      // Why: the v8.1.1 per-VARIANT dedup (Doc 2 §6.3's "same-id property hashes
      // collide" reading, disproved by the same test) left 97.8% of bundle confirms
      // multi-POST (export 09-03→09-05: 399 of 408), and mobile trips Shopify's
      // request-velocity challenge at the 4th–7th /cart/add.js of a Confirm
      // (every 429 in the export is mobile; first-attempt trips at POST 4,4,5,6,6,7).
      // The one real ceiling is Shopify's form-field count on the (form-encoded)
      // request, not the line count: live 2026-09-07, 101 lines / ~3.4k fields
      // → 200, 121 lines / ~4k fields → HTTP 400 (201 lines pass as JSON, which
      // `_bundle_raw` cannot use). Pack children under FORM_FIELD_BUDGET so a
      // 60- or 90-item bundle becomes two or three POSTs instead of one 400.
      var seenHash = {};
      var bundleGroups = [];
      var current = [parent];
      var currentFields = _itemFieldCount(parent);
      var deferred = [];
      for (var j = 0; j < children.length; j++) {
        var h = String(children[j].id) + '#' + _stablePropertyHash(children[j].properties);
        if (seenHash[h]) { deferred.push(children[j]); continue; }
        seenHash[h] = true;
        var fc = _itemFieldCount(children[j]);
        if (current.length > 0 && currentFields + fc > FORM_FIELD_BUDGET) {
          bundleGroups.push(current);
          current = [];
          currentFields = 0;
        }
        current.push(children[j]);
        currentFields += fc;
      }
      if (current.length) bundleGroups.push(current);
      var chunked = bundleGroups.length > 1;
      for (var d = 0; d < deferred.length; d++) {
        bundleGroups.push([deferred[d]]);
      }
      bundleGroups.chunked = chunked;           // v8.2.0 — split by the form-field budget
      bundleGroups.deferredCount = deferred.length;
      return bundleGroups;
    }

    // Multistep — v8.2.0: ONE POST for every size (parents + children), packed
    // under the same form-field budget as bundles. Same live basis as the bundle
    // change (2026-09-06): distinct-variant parents never merge, and the
    // same-variant children differ on _ParentVariant / _lineItemKey, so Shopify
    // keeps every line. Before this, N sizes were N POSTs — and mobile trips the
    // request-velocity challenge at the 4th–7th POST. Order kept as the builder
    // emits it (parents first, then children); the display transform reverses
    // the group so Shopify's newest-first cart shows the garments on top.
    var msGroups = [];
    var msCurrent = [];
    var msFields = 0;
    for (var mi = 0; mi < items.length; mi++) {
      var mfc = _itemFieldCount(items[mi]);
      if (msCurrent.length > 0 && msFields + mfc > FORM_FIELD_BUDGET) { msGroups.push(msCurrent); msCurrent = []; msFields = 0; }
      msCurrent.push(items[mi]);
      msFields += mfc;
    }
    if (msCurrent.length) msGroups.push(msCurrent);
    var msParents = 0;
    for (var mp = 0; mp < items.length; mp++) { if ((items[mp].properties || {})._ParentVariant === undefined) msParents++; }
    msGroups.parentCount = msParents;
    msGroups.chunked = msGroups.length > 1;
    return msGroups;

    // v8.1.1 grouping (one POST per size parent) — unreachable, kept for history.
    var groupsByParent = {};
    var parentOrder = [];
    for (var k = 0; k < items.length; k++) {
      var item = items[k];
      var p = item.properties || {};
      if (p._ParentVariant === undefined) {
        groupsByParent[item.id] = [item];
        parentOrder.push(item.id);
      } else {
        var parentVariant = parseInt(p._ParentVariant, 10);
        if (!groupsByParent[parentVariant]) {
          // Orphaned child — no matching parent in payload. Defensive
          // fall-back: put it in its own POST.
          groupsByParent[parentVariant] = [];
          parentOrder.push(parentVariant);
        }
        groupsByParent[parentVariant].push(item);
      }
    }
    var out = [];
    for (var oi = 0; oi < parentOrder.length; oi++) {
      out.push(groupsByParent[parentOrder[oi]]);
    }
    return out;
  }

  /**
   * B1 — order groups (and items within each group) so that Shopify's
   * newest-first cart/checkout display reproduces the builder's intended
   * order (parent above its children). Pure reorder: no item, quantity or
   * property is changed, added or dropped; verifyAfterSubmit's compound-key
   * matching is order-agnostic so verification is unaffected.
   *
   * @param {Array<Array>} groups - output of _groupItemsForSubmit
   * @returns {Array<Array>} new array; input groups are not mutated
   */
  function _orderGroupsForDisplay(groups) {
    var out = [];
    out.chunked = groups.chunked; out.deferredCount = groups.deferredCount; out.parentCount = groups.parentCount; // v8.2.0
    for (var i = groups.length - 1; i >= 0; i--) {
      var g = groups[i];
      var reversed = [];
      for (var j = g.length - 1; j >= 0; j--) reversed.push(g[j]);
      out.push(reversed);
    }
    return out;
  }

  // v8.2.0 — form-field budget for one /cart/add.js (see _groupItemsForSubmit).
  var FORM_FIELD_BUDGET = 3500; // live 2026-09-07: 4,022 fields → 200, 4,220 → 400 (cap ≈ 4,096); 3,500 leaves headroom
  function _fieldCount(v) {
    if (v === null || typeof v === 'undefined' || typeof v !== 'object') return 1;
    var n = 0;
    for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k)) n += _fieldCount(v[k]); }
    return n;
  }
  function _itemFieldCount(item) { return 2 + _fieldCount((item && item.properties) || {}); } // id + quantity + leaves

  // v8.2.0 — order-independent, type-stable serialisation of a line's properties:
  // two entries merge inside one /cart/add.js only when this is equal AND the
  // variant id is equal (live-verified 2026-09-07).
  function _stablePropertyHash(props) {
    function norm(v) {
      if (v === null || typeof v === 'undefined') return '';
      if (typeof v !== 'object') return String(v);
      var keys = Object.keys(v).sort();
      var o = {};
      for (var i = 0; i < keys.length; i++) o[keys[i]] = norm(v[keys[i]]);
      return o;
    }
    try { return JSON.stringify(norm(props || {})); } catch (e) { return String(Math.random()); }
  }

  function _describePattern(type, groups) {
    // v8.1.1 — a bundle that fits in one batch POST reports 'bundle_batch';
    // only bundles with deferred same-variant children remain 'bundle_sequential'.
    // v8.2.0 — deferral happens only for byte-identical duplicate entries, so
    // 'bundle_sequential' should now be rare; the pattern names are unchanged.
    // Multistep v8.2.0: 'single_atc' (one size), 'multistep_batch' (several sizes,
    // one POST), 'sequential_per_size' only when the form-field budget split it.
    if (type === 'bundle') {
      if (groups.length <= 1) return 'bundle_batch';
      // v8.2.0 — several groups because the bundle exceeded the form-field budget
      // (60/90-item bundles): 'bundle_chunked'. Only exact-duplicate deferrals keep
      // the old 'bundle_sequential' name.
      if (groups.chunked && !groups.deferredCount) return 'bundle_chunked';
      return 'bundle_sequential';
    }
    if (groups.length > 1) return 'sequential_per_size';
    return (groups.parentCount || 0) > 1 ? 'multistep_batch' : 'single_atc';
  }

  /**
   * Sequentially POST each group. Stops on first failure with
   * partialSuccess if any prior POSTs succeeded.
   */
  var _lastGroupResults = []; // v8.1.2 diagnostics — reset per submit()
  var _lastPostAtMs = 0;      // v8.2.0 — when the last /cart/add.js of this submit resolved

  var _splitsSoFar = 0;       // v8.2.0 — groups halved after an HTTP 400 in this submit()
  function _postSequentially(groups, accumulated, index, pattern) {
    // (reset per submit() in submit(), not here — a 400 split re-enters at index 0)
    if (index >= groups.length) {
      return Promise.resolve({
        ok: true,
        addedItems: accumulated,
        pattern: pattern,
        groupResults: _lastGroupResults.slice(),
        groupSplits: _splitsSoFar   // v8.2.0
      });
    }

    var groupItems = groups[index];
    var addPayload = { items: groupItems };
    // v8.1.1 — refresh /cart.js once, after the LAST group, not after every POST.
    var isLast = index === groups.length - 1;

    return JRS.CartAPI.addItems(addPayload, { skipCartRefresh: !isLast })
      .then(function (result) {
        if (result && !result.ok && result.httpStatus === 400 && groupItems.length > 1 && _splitsSoFar < 8) {
          // v8.2.0 — HTTP 400 on a multi-line group is the form-field ceiling (never a
          // stock or challenge answer): halve the group and carry on. Nothing was added
          // by the refused request, so nothing is duplicated.
          _splitsSoFar++;
          var half = Math.ceil(groupItems.length / 2);
          var splitGroups = groups.slice(0, index).concat([groupItems.slice(0, half), groupItems.slice(half)], groups.slice(index + 1));
          return _postSequentially(splitGroups, accumulated, index, pattern);
        }
        if (!result || !result.ok) {
          var failed = {
            ok: false,
            partialSuccess: accumulated.length > 0,
            addedItems: accumulated,
            failedIndex: index,
            groupCount: groups.length,
            failedGroup: _describeFailedGroup(groupItems),
            httpStatus: (result && typeof result.httpStatus === 'number') ? result.httpStatus : null,
            shopifyDescription: (result && result.shopifyDescription) || '',
            challenge: !!(result && result.challenge),
            stockFailure: classifyStockFailure(result && result.shopifyDescription),   // v8.1.4
            pattern: pattern,
            error: (result && result.error) || 'unknown'
          };
          // v8.2.0 — a 422 on a group holding several garments (one-POST multistep)
          // is a partial add: Shopify caps the short size and still writes the rest.
          // Read the cart once and report what landed, so the review step never
          // says "nothing was added" (and never re-enables a duplicating Confirm)
          // when the other sizes are in the cart.
          if (failed.httpStatus === 422 && failed.failedGroup.garmentCount > 1 && JRS.CartAPI && typeof JRS.CartAPI.getCart === 'function') {
            return JRS.CartAPI.getCart().then(function (cartNow) {
              var missingNow = _findMissingExpectedItems({ items: groupItems }, cartNow); // returns the expected item objects
              var landed = [];
              for (var li = 0; li < groupItems.length; li++) { if (missingNow.indexOf(groupItems[li]) === -1) landed.push(groupItems[li]); }
              failed.addedItems = accumulated.concat(landed);
              failed.partialSuccess = failed.addedItems.length > 0;
              failed.landedInFailedGroup = landed.length;
              return failed;
            }).catch(function () { return failed; });
          }
          return failed;
        }
        // v8.1.2 — record what Shopify said it added for THIS group (the
        // /cart/add.js response echoes the added lines). If a later
        // verification cannot find lines that a 2xx response listed, this is
        // the evidence that separates "never landed" from "landed then removed".
        _lastPostAtMs = Date.now(); // v8.2.0
        try {
          var respItems = (result && result.response && result.response.items) || [];
          _lastGroupResults.push({
            index: index,
            posted: groupItems.length,
            postedQty: groupItems.reduce(function (n, it) { return n + Number(it.quantity || 0); }, 0),
            responded: respItems.length,
            respondedQty: respItems.reduce(function (n, it) { return n + Number(it.quantity || 0); }, 0)
          });
        } catch (eDiag) {}
        var nextAccum = accumulated.concat(groupItems);
        return _postSequentially(groups, nextAccum, index + 1, pattern);
      })
      .catch(function (err) {
        return {
          ok: false,
          partialSuccess: accumulated.length > 0,
          addedItems: accumulated,
          failedIndex: index,
          groupCount: groups.length,
          failedGroup: _describeFailedGroup(groupItems),
          httpStatus: (err && typeof err.status === 'number') ? err.status : 0,
          shopifyDescription: (err && err.data && err.data.description) || '',
          challenge: !!(err && err.challenge),
          pattern: pattern,
          error: (err && err.message) || 'unknown'
        };
      });
  }

  // Instrumentation (2026-09-03) — describe the POST group that failed so
  // pdp_customizer_confirm_failed can name the child variant + requested qty
  // without a repro. Prefers the garment line (a child carries _ParentVariant;
  // a bundle parent carries _bundle) — the decoration lines never sell out.
  function _describeFailedGroup(items) {
    var list = items || [];
    // v8.1.4 — attribute the failure to the GARMENT (the line without _ParentVariant).
    // Shopify's stock checks apply to the garment variant; decoration variants
    // (the children) carry no inventory and are never what a 422 refuses. The
    // previous pick (first child) reported "Child variant 43625659990259" on the
    // 2026-09-04 07:37 case and sent the investigation to the wrong variants.
    var garment = null, child = null;
    var garmentIds = [];   // v8.2.0 — a multi-size multistep group carries several garments
    for (var i = 0; i < list.length; i++) {
      var props = (list[i] && list[i].properties) || {};
      if (props._ParentVariant === undefined) { if (!garment) garment = list[i]; garmentIds.push(Number(list[i].id || 0)); }
      else if (!child) child = list[i];
    }
    var pick = garment || child || list[0] || null;
    var totalQty = 0;
    for (var j = 0; j < list.length; j++) totalQty += Number((list[j] && list[j].quantity) || 0);
    return {
      itemCount: list.length,
      totalQty: totalQty,
      // failed item = the garment when the group has one
      variantId: pick ? Number(pick.id || 0) : 0,
      requestedQty: pick ? Number(pick.quantity || 0) : 0,
      isGarment: !!garment,
      // kept for continuity with the 8.1.1 field — the decoration line in the group
      childVariantId: child ? Number(child.id || 0) : 0,
      childRequestedQty: child ? Number(child.quantity || 0) : 0,
      garmentVariantId: garment ? Number(garment.id || 0) : 0,
      garmentVariantIds: garmentIds,                 // v8.2.0
      garmentCount: garmentIds.length,               // v8.2.0
      garmentRequestedQty: garment ? Number(garment.quantity || 0) : 0,
      // v8.1.4b — scope for the branding correction (which lines belong to THIS add)
      bundleInstanceId: String(((garment || child || {}).properties || {})._bundle_instance_id || ''),
      customizationInstanceId: String(((garment || child || {}).properties || {})._customization_instance_id || ''),
      isBundle: !!(garment && (garment.properties || {})._bundle !== undefined)
    };
  }

  /**
   * v8.1.4b — after a 422 "Only N items were added" on a group, Shopify has capped the
   * inventory-tracked line (the garment, or the bundle parent) and added the untracked
   * decoration lines at the FULL requested quantity (live: garment 8, branding 90 —
   * £315 of branding for goods not in the cart). Bring every branding line of that
   * group down to what actually landed:
   *   multistep — child qty := landed garment qty
   *   bundle    — child qty := child._qty (per single bundle) × landed parent qty
   * One /cart/change.js per branding line, sequential. Never blocks; reports what it did.
   *
   * @param {Object} failedResult  the ok:false submit result (failedGroup + stockFailure)
   * @returns {Promise<{ok:boolean, landedQty:number, corrected:Array, failed:Array, error:string|null}>}
   */
  // v8.2.0 — a one-POST multistep group can hold several garments; correct each.
  function correctBrandingAfterPartial(failedResult) {
    var fg0 = failedResult && failedResult.failedGroup;
    var ids = (fg0 && fg0.garmentVariantIds && fg0.garmentVariantIds.length > 1 && !fg0.isBundle) ? fg0.garmentVariantIds : null;
    if (!ids) return _correctBrandingForGarment(failedResult);
    var merged = { ok: true, landedQty: 0, corrected: [], failed: [], error: null, perGarment: [] };
    var chain = Promise.resolve();
    ids.forEach(function (gid) {
      chain = chain.then(function () {
        var fgOne = {}; for (var k in fg0) { if (Object.prototype.hasOwnProperty.call(fg0, k)) fgOne[k] = fg0[k]; }
        fgOne.garmentVariantId = gid;
        return _correctBrandingForGarment({ failedGroup: fgOne }).then(function (r) {
          merged.perGarment.push({ garmentVariantId: gid, ok: r.ok, landedQty: r.landedQty, error: r.error });
          merged.corrected = merged.corrected.concat(r.corrected || []);
          merged.failed = merged.failed.concat(r.failed || []);
          if (!merged.landedQty) merged.landedQty = r.landedQty || 0;
          if (!r.ok && r.error && r.error !== 'garment_line_not_found') { merged.ok = false; merged.error = merged.error || r.error; }
          if (!r.ok && (r.failed || []).length) merged.ok = false;
        });
      });
    });
    return chain.then(function () { return merged; });
  }

  function _correctBrandingForGarment(failedResult) {
    var fg = failedResult && failedResult.failedGroup;
    var out = { ok: false, landedQty: 0, corrected: [], failed: [], error: null };
    if (!fg || !fg.garmentVariantId) { out.error = 'no_garment_in_group'; return Promise.resolve(out); }
    if (!JRS.CartAPI || typeof JRS.CartAPI.getCart !== 'function' || typeof JRS.CartAPI.changeItem !== 'function') {
      out.error = 'cart_api_unavailable'; return Promise.resolve(out);
    }
    var inst = fg.bundleInstanceId || fg.customizationInstanceId || '';
    var instKey = fg.bundleInstanceId ? '_bundle_instance_id' : '_customization_instance_id';
    return JRS.CartAPI.getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var garmentLine = null;
      var children = [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i]; var p = it.properties || {};
        if (inst && String(p[instKey] || '') !== inst) continue;
        if (Number(it.variant_id) === fg.garmentVariantId && p._ParentVariant === undefined) { garmentLine = it; continue; }
        if (String(p._ParentVariant || '') === String(fg.garmentVariantId)) children.push(it);
      }
      if (!garmentLine) { out.error = 'garment_line_not_found'; return out; }
      out.landedQty = Number(garmentLine.quantity || 0);
      var targets = [];
      for (var c = 0; c < children.length; c++) {
        var cp = children[c].properties || {};
        var target = fg.isBundle
          ? Number(cp._qty || 0) * out.landedQty   // per-bundle qty × bundles that landed
          : out.landedQty;
        if (Number(children[c].quantity || 0) > target) {
          targets.push({
            key: String(children[c].key), variantId: Number(children[c].variant_id),
            lineItemKey: String(cp._lineItemKey || ''),
            // v8.1.5b — per-size bundle children of one position share _lineItemKey (Doc 2 §13.12);
            // only _ProductVariantId / _ProductSize tell them apart when a key must be re-resolved.
            productVariantId: String(cp._ProductVariantId || ''), productSize: String(cp._ProductSize || ''),
            from: Number(children[c].quantity || 0), to: target
          });
        }
      }
      if (targets.length === 0) { out.ok = true; return out; }
      // Shopify can re-key OTHER lines after a /cart/change.js (live: the second
      // branding line's key was refused after the first change). Re-resolve every
      // target from the freshest cart by its stable identity — same instance id,
      // same _ParentVariant, same _lineItemKey, same variant — before each change.
      var usedKeys = {};
      function resolveKey(latestCart, t) {
        var list = (latestCart && latestCart.items) || [];
        var i, li, lp;
        // v8.1.5b — the line's own key first: it survives unless Shopify re-keyed that line.
        for (i = 0; i < list.length; i++) {
          li = list[i];
          if (String(li.key) === t.key && Number(li.variant_id) === t.variantId && !usedKeys[t.key]) {
            return Number(li.quantity || 0) === t.to ? null : t.key;
          }
        }
        for (i = 0; i < list.length; i++) {
          li = list[i]; lp = li.properties || {};
          if (usedKeys[String(li.key)]) continue; // v8.1.5b — never re-target a line already corrected
          if (inst && String(lp[instKey] || '') !== inst) continue;
          if (String(lp._ParentVariant || '') !== String(fg.garmentVariantId)) continue;
          if (Number(li.variant_id) !== t.variantId) continue;
          if (t.lineItemKey && String(lp._lineItemKey || '') !== t.lineItemKey) continue;
          if (t.productVariantId && String(lp._ProductVariantId || '') !== t.productVariantId) continue; // v8.1.5b
          if (t.productSize && String(lp._ProductSize || '') !== t.productSize) continue;                // v8.1.5b
          if (Number(li.quantity || 0) === t.to) return null; // already corrected (merged / re-keyed)
          return String(li.key);
        }
        return null;
      }
      function step(idx, latestCart) {
        if (idx >= targets.length) { out.ok = out.failed.length === 0; return Promise.resolve(out); }
        var t = targets[idx];
        var key = idx === 0 ? t.key : resolveKey(latestCart, t);
        if (idx > 0 && key === null) { out.corrected.push(t); return step(idx + 1, latestCart); } // nothing left to change
        return JRS.CartAPI.changeItem(key, { quantity: t.to }).then(function (res) {
          if (res && res.ok) { usedKeys[key] = true; out.corrected.push(t); return step(idx + 1, res.cart || latestCart); }
          t.error = (res && res.error) || 'change_failed'; out.failed.push(t);
          return (res && res.cart) ? step(idx + 1, res.cart) : JRS.CartAPI.getCart().then(function (c2) { return step(idx + 1, c2); }).catch(function () { return step(idx + 1, latestCart); });
        }).catch(function (err) {
          t.error = (err && err.message) || 'threw'; out.failed.push(t);
          return step(idx + 1, latestCart);
        });
      }
      return step(0, cart);
    }).catch(function (err) {
      out.error = (err && err.message) || 'cart_read_failed';
      return out;
    });
  }

  // v8.1.4 — classify Shopify's 422 `description` (its body is only
  // {status, message, description}; no variant id — only the sold-out text
  // names the product + variant title). One classification feeds both the
  // shopper message and the failure_class telemetry field.
  function classifyStockFailure(description) {
    var d = String(description || '');
    var m;
    if (/maximum quantity of this item is already in your cart/i.test(d)) {
      return { failureClass: 'already_in_cart', acceptedQty: 0 };
    }
    if ((m = d.match(/Only (\d+) items? (?:was|were) added to your cart due to availability/i))) {
      return { failureClass: 'partial_availability', acceptedQty: Number(m[1]) };
    }
    if ((m = d.match(/The product '(.+?)' is already sold out/i))) {
      var named = m[1];
      var parts = named.split(' - ');
      return {
        failureClass: 'sold_out', acceptedQty: 0,
        namedProduct: parts.length > 1 ? parts.slice(0, -1).join(' - ') : named,
        namedVariantTitle: parts.length > 1 ? parts[parts.length - 1] : ''
      };
    }
    if (/^All (\d+) .* are in your cart/i.test(d)) {
      return { failureClass: 'already_in_cart', acceptedQty: 0 };
    }
    return { failureClass: 'other', acceptedQty: null };
  }

  // -------------------------------------------------------------------
  // Post-submit verification — Doc 2 §10.4 compound-key matching
  // -------------------------------------------------------------------

  /**
   * After all POSTs land, verify the cart contains the expected items.
   *
   * Steps:
   *   1. Fetch current cart via JRS.CartAPI.getCart()
   *   2. Run JRS.CartValidator.validateCart(cart) — heal pass that fixes
   *      drift (orphans, qty mismatches, missing children, etc.). The
   *      validator is async and dispatches its own cart-update events.
   *   3. Re-fetch cart (in case healing changed it)
   *   4. Match expected items from `expectedPayload.items[]` against
   *      observed cart items using compound-key matching:
   *        Multistep children: `_lineItemKey`
   *        Bundle children:    `_lineItemKey` + `_ProductVariantId` +
   *                            `_ProductSize` (Doc 2 §10.4)
   *        Parents:            variantId (multistep) or `_bundle_instance_id`
   *                            (bundle)
   *   5. Return { ok, missing: [...] } where missing lists any expected
   *      item whose compound key didn't appear in the cart.
   *
   * @param {Object} expectedPayload - the cart payload that should have
   *                                    landed (output of CartPayloadBuilder.build)
   * @returns {Promise<{ok, missing?, healed?}>}
   */
  function verifyAfterSubmit(expectedPayload) {
    if (!JRS.CartAPI || typeof JRS.CartAPI.getCart !== 'function') {
      return Promise.reject(new Error('CartSubmit.verifyAfterSubmit: JRS.CartAPI unavailable'));
    }

    var hasValidator = JRS.CartValidator && typeof JRS.CartValidator.validateCart === 'function';
    var expectedCount = ((expectedPayload && expectedPayload.items) || []).length;
    var lastPostAt = _lastPostAtMs;
    var reads = 0;
    var reread = false;
    var maxWm = null;
    var verifyDiag = { watermarkStale: null, updatedAt: null, lastPostAt: lastPostAt ? Math.floor(lastPostAt / 1000) : null,
      bypassRead: false, bypassFound: null, unverifiable: false, bypassStatus: 0, bypassChallenge: false };
    var wmOf = function (c) { return (c && typeof c._cartjsUpdatedAt === 'number') ? c._cartjsUpdatedAt : null; };
    var noteWm = function (c) { var wm = wmOf(c); if (wm !== null && (maxWm === null || wm > maxWm)) maxWm = wm; };
    // v8.1.5 — a read is provably stale when its watermark is below an earlier read's
    // (monotonic violation) or more than 1 s behind the last POST.
    var stale = function (c) {
      var wm = wmOf(c);
      if (wm === null) return null;
      if (maxWm !== null && wm < maxWm) return true;
      if (lastPostAt && wm < Math.floor(lastPostAt / 1000) - 1) return true;
      return false;
    };
    var missingIn = function (c) { return _findMissingExpectedItems(expectedPayload, c); };
    var read = function () {
      return JRS.CartAPI.getCart().then(function (c) {
        reads++;
        var was = stale(c); noteWm(c);
        if (was === true) verifyDiag.watermarkStale = true; else if (verifyDiag.watermarkStale === null) verifyDiag.watermarkStale = was;
        verifyDiag.updatedAt = wmOf(c);
        return c;
      });
    };
    var wait = function (ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); };
    // v8.1.5 escalation — re-read at 1.5 / 3 / 6 s while a read is provably stale, then ONE
    // authoritative write-path read. Resolves { cart, missing } for the settled state.
    var settle = function (c0, m0) {
      var delays = [1500, 3000, 6000];
      var lastCart = c0, lastMissing = m0;
      var attempt = function (i) {
        if (i >= delays.length) return Promise.resolve();
        if (i > 0 && stale(lastCart) === false) return Promise.resolve();
        return wait(delays[i]).then(function () {
          reread = true;
          return read().then(function (c) {
            lastCart = c; lastMissing = missingIn(c);
            if (lastMissing.length === 0) return;
            return attempt(i + 1);
          }).catch(function () { return attempt(i + 1); });
        });
      };
      return attempt(0).then(function () {
        if (lastMissing.length === 0) return { cart: lastCart, missing: lastMissing };
        if (!JRS.CartAPI || typeof JRS.CartAPI.readCartAuthoritative !== 'function') return { cart: lastCart, missing: lastMissing };
        verifyDiag.bypassRead = true;
        return JRS.CartAPI.readCartAuthoritative().then(function (authCart) {
          reads++;
          var missingAuth = missingIn(authCart);
          verifyDiag.bypassFound = missingAuth.length === 0;
          return { cart: authCart, missing: missingAuth };
        }).catch(function (bypassErr) {
          // v8.1.5b — could not CHECK; never reported as a miss.
          verifyDiag.bypassFound = null;
          verifyDiag.unverifiable = true;
          verifyDiag.bypassStatus = (bypassErr && typeof bypassErr.status === 'number') ? bypassErr.status : 0;
          verifyDiag.bypassChallenge = !!(bypassErr && bypassErr.challenge);
          return { cart: lastCart, missing: lastMissing };
        });
      });
    };
    var heal = function (c) {
      return hasValidator
        ? JRS.CartValidator.validateCart(c).catch(function () { return { valid: false, issues: [], issuesFixed: 0 }; })
        : Promise.resolve({ valid: true, issues: [], issuesFixed: 0 });
    };
    var preHealMissingCount = 0, preHealLineCount = 0;
    var finish = function (finalCart, missing, healResult, healSkipped) {
      healResult = healResult || { issues: [], issuesFixed: 0 };
      var diag = {
        verifyReads: reads,
        verifyWatermarkStale: verifyDiag.watermarkStale,
        verifyUpdatedAt: verifyDiag.updatedAt,
        verifyLastPostAt: verifyDiag.lastPostAt,
        verifyBypassRead: verifyDiag.bypassRead,
        verifyBypassFound: verifyDiag.bypassFound,
        expectedLineCount: expectedCount,
        matchedLineCount: expectedCount - missing.length,
        cartLineCountAfter: (finalCart && finalCart.items && finalCart.items.length) || 0,
        cartUnitCountAfter: (finalCart && typeof finalCart.item_count === 'number') ? finalCart.item_count : -1,
        preHealMissingCount: preHealMissingCount,
        preHealLineCount: preHealLineCount,
        healIssuesFound: (healResult.issues && healResult.issues.length) || 0,
        healed: healResult.issuesFixed || 0,
        healSkipped: !!healSkipped,   // v8.2.0 — the heal never runs on a read that lacks expected lines
        verifyReread: reread,
        missingLines: missing.slice(0, 12).map(function (m) {
          var p = m.properties || {};
          return (p._ParentVariant !== undefined ? 'child:' : 'parent:') + String(m.id) + ':' + String(m.quantity || 0);
        }).join(',')
      };
      var r = { ok: missing.length === 0, missing: missing, healed: healResult.issuesFixed || 0, diagnostics: diag };
      if (verifyDiag.unverifiable) {
        r.unverifiable = true;
        r.bypassStatus = verifyDiag.bypassStatus;
        r.bypassChallenge = verifyDiag.bypassChallenge;
        diag.verifyBypassStatus = verifyDiag.bypassStatus;
      }
      return r;
    };

    return read().then(function (cart) {
      var preHealMissing = missingIn(cart);
      preHealMissingCount = preHealMissing.length;
      preHealLineCount = (cart && cart.items && cart.items.length) || 0;

      if (preHealMissing.length === 0) {
        // Clean first read: heal (idempotent drift repair), then confirm.
        return heal(cart).then(function (hr) {
          return read().then(function (post) {
            var m = missingIn(post);
            if (m.length === 0) return finish(post, m, hr, false);
            return settle(post, m).then(function (st) { return finish(st.cart, st.missing, hr, false); });
          });
        });
      }

      // v8.2.0 — the first read lacks expected lines. Settle the read (escalating
      // re-reads, then the write-path read) BEFORE any heal: on 2026-09-05 20:18 a
      // heal ran on a read that had not yet seen the last POST, judged the other
      // lines of the instance orphans and removed them (item_count 3 → 2 → 1 → 0).
      // A heal never runs on a cart that is short of expected lines.
      return settle(cart, preHealMissing).then(function (st) {
        if (st.missing.length > 0 || verifyDiag.unverifiable) return finish(st.cart, st.missing, null, true);
        return heal(st.cart).then(function (hr) {
          // The settled cart is authoritative; a fresh cached read could be stale again.
          // Re-read only if the heal actually changed something, and settle that read too.
          if (!(hr && hr.issuesFixed > 0)) return finish(st.cart, [], hr, false);
          return read().then(function (post) {
            var m = missingIn(post);
            if (m.length === 0) return finish(post, m, hr, false);
            return settle(post, m).then(function (st2) { return finish(st2.cart, st2.missing, hr, false); });
          });
        });
      });
    });
  }

  /**
   * Find expected items that didn't make it into the cart per Doc 2
   * §10.4 compound-key matching.
   *
   * @param {Object} expectedPayload - { items: [...] }
   * @param {Object} cart - Shopify cart response
   * @returns {Array<Object>} missing items (subset of expectedPayload.items)
   */
  function _findMissingExpectedItems(expectedPayload, cart) {
    var expected = (expectedPayload && expectedPayload.items) || [];
    var observed = (cart && cart.items) || [];
    var missing = [];

    for (var i = 0; i < expected.length; i++) {
      if (!_findMatchingCartItem(expected[i], observed)) {
        missing.push(expected[i]);
      }
    }
    return missing;
  }

  function _findMatchingCartItem(expectedItem, observedItems) {
    var expProps = expectedItem.properties || {};
    var isChild = expProps._ParentVariant !== undefined;
    var expLineKey = expProps._lineItemKey || null;
    var expProductVariant = expProps._ProductVariantId || null;
    var expProductSize = expProps._ProductSize || null;

    for (var i = 0; i < observedItems.length; i++) {
      var obs = observedItems[i];
      var obsProps = obs.properties || {};

      if (isChild) {
        // Child match: compound key
        if (!expLineKey) continue;
        if (obsProps._lineItemKey !== expLineKey) continue;
        // For bundle children, also require _ProductVariantId + _ProductSize
        if (expProductVariant) {
          if (obsProps._ProductVariantId !== expProductVariant) continue;
        }
        if (expProductSize) {
          if (obsProps._ProductSize !== expProductSize) continue;
        }
        return obs;
      }

      // Parent match: variantId. For bundles, _bundle_instance_id is the
      // additional discriminator if multiple bundles are in the cart.
      if (Number(obs.variant_id || obs.id) !== Number(expectedItem.id)) continue;
      if (expProps._bundle_instance_id) {
        if (obsProps._bundle_instance_id !== expProps._bundle_instance_id) continue;
      }
      // Duplicate same-variant STANDARD adds: the row-scope id disambiguates
      // which parent this expectation refers to (2026-08-02).
      if (expProps._customization_instance_id) {
        if (obsProps._customization_instance_id !== expProps._customization_instance_id) continue;
      }
      return obs;
    }
    return null;
  }

  /**
   * Convenience: submit + verify in one call.
   *
   * @param {Object} productConfiguration
   * @returns {Promise<{ok, addedItems, missing?, healed?, partialSuccess?, failedIndex?, error?, pattern}>}
   */
  // Bounded add-manifest witness (Doc 10 P0). After every add, record a compact
  // "what this add intended + did it verify" entry to the _jrs_add_manifest cart
  // attribute (JRS.AddManifest — a ring of the last 5 adds, keepalive/sendBeacon).
  // This survives the vanished-line case where a line's own per-line log dies with
  // it. Best-effort: never throws, never blocks the confirm flow, no-op if the
  // module isn't loaded. `expected` maps each child _lineItemKey → total qty from
  // the payload that was actually POSTed; `verdict` is the verification outcome.
  function _recordAddManifest(expectedPayload, verdict) {
    if (!(window.JRS && JRS.AddManifest && typeof JRS.AddManifest.record === 'function')) return;
    try {
      var items = (expectedPayload && expectedPayload.items) || [];
      var expected = {};
      var id = '';
      var flow = 'customizer';
      for (var i = 0; i < items.length; i++) {
        var props = (items[i] && items[i].properties) || {};
        var key = props._lineItemKey;
        if (key) expected[key] = (expected[key] || 0) + (Number(items[i].quantity) || 1);
        if (props._bundle_instance_id) { id = props._bundle_instance_id; flow = 'bundle'; }
        else if (!id && props._ParentVariant) id = String(props._ParentVariant);
      }
      var write = function (existingRaw, cartLines) {
        JRS.AddManifest.record({
          timestamp: _mintTimestamp(),
          id: id,
          flow: flow,
          verdict: verdict,
          expected: expected,
          existingRaw: existingRaw,
          // Membership prune (2026-08-04): old ring entries survive only while
          // they still reference a line in the live cart. Omitted on the
          // fallback path → no pruning (fail open).
          cartLines: cartLines
        });
      };
      // Read the current attribute so the ring APPENDS (last 5 adds) rather than
      // overwriting. If getCart is unavailable/fails, still record a fresh ring.
      if (JRS.CartAPI && typeof JRS.CartAPI.getCart === 'function') {
        JRS.CartAPI.getCart().then(function (cart) {
          write(
            (cart && cart.attributes) ? cart.attributes[JRS.AddManifest.ATTR_KEY] : null,
            (cart && cart.items) ? cart.items : undefined
          );
        }).catch(function () { write(null); });
      } else {
        write(null);
      }
    } catch (e) { /* best-effort witness — swallow */ }
  }

  /**
   * Defect B (2026-08-01) — overwrite the `_bundle_debug_log` CART ATTRIBUTE
   * on every v2 bundle add. Cart attributes persist across theme switches, so
   * a cart seeded by the LEGACY/control bundle flow can carry a
   * control-stamped log into a v2-built order and misattribute the A/B arm in
   * analysis. v2 does its bundle logging elsewhere (_jrs_add_manifest +
   * per-line witnesses), so this key is superseded: stamp a compact v2 record
   * in its place. Best-effort fire-and-forget — never blocks the purchase.
   */
  function _stampBundleDebugLog() {
    if (!JRS.CartAPI || typeof JRS.CartAPI.updateAttributes !== 'function') return;
    try {
      var record = {
        theme: 'base',
        system: 'v3',
        build: _readBuildId(),
        ts: _mintTimestamp(),
        note: 'v2 bundle add — legacy _bundle_debug_log superseded (logging lives in _jrs_add_manifest)'
      };
      JRS.CartAPI.updateAttributes({ '_bundle_debug_log': JSON.stringify(record) })
        .catch(function () { /* best-effort */ });
    } catch (e) { /* best-effort — never blocks the add */ }
  }

  function submitAndVerify(productConfiguration) {
    // Preflight build — surfaces builder validation errors (missing
    // catalogue descriptor, invalid configuration, etc.) BEFORE any
    // /cart/add.js POST hits the wire. The resulting payload is
    // discarded after this point because submit() will do its own
    // build internally and that's the one whose _bundle_instance_id /
    // _time / timestamps actually land in Shopify. Using THIS preflight
    // payload for verification (as the previous code did) caused bundle
    // parent matches to fail because the verifier's expected
    // _bundle_instance_id (random suffix minted here) drifted from the
    // observed one (random suffix minted again inside submit).
    //
    // Same existence check as submit(). Without it a missing builder threw a
    // raw "Cannot read properties of undefined (reading 'build')" here.
    if (!JRS.CartPayloadBuilder || typeof JRS.CartPayloadBuilder.build !== 'function') {
      return Promise.reject(new Error('CartSubmit.submitAndVerify: JRS.CartPayloadBuilder unavailable'));
    }
    try {
      JRS.CartPayloadBuilder.build(productConfiguration);
    } catch (err) {
      return Promise.reject(err);
    }

    return submit(productConfiguration).then(function (submitResult) {
      if (!submitResult.ok) {
        // Submit failed — skip verification, return the submit result
        return submitResult;
      }
      // Defect B — every successful v2 BUNDLE add supersedes any legacy
      // control-stamped _bundle_debug_log cart attribute (see the helper).
      if (productConfiguration && productConfiguration.type === 'bundle') {
        _stampBundleDebugLog();
      }
      // Use the payload that was ACTUALLY POSTed (submit attached it
      // as submitResult.expectedPayload) so the verifier matches
      // against the exact _bundle_instance_id Shopify received.
      var actualPayload = submitResult.expectedPayload;
      return verifyAfterSubmit(actualPayload).then(function (verifyResult) {
        var verdict = !verifyResult.ok ? 'missing' : (verifyResult.healed ? 'recovered' : 'verified');
        _recordAddManifest(actualPayload, verdict);
        return {
          ok: verifyResult.ok,
          addedItems: submitResult.addedItems,
          pattern: submitResult.pattern,
          missing: verifyResult.missing,
          healed: verifyResult.healed,
          diagnostics: verifyResult.diagnostics || null,   // v8.1.2
          groupResults: submitResult.groupResults || [],    // v8.1.2
          groupSplits: submitResult.groupSplits || 0,        // v8.2.0
          unverifiable: !!verifyResult.unverifiable,        // v8.1.5b — could not check, not a miss
          bypassStatus: verifyResult.bypassStatus || 0,
          bypassChallenge: !!verifyResult.bypassChallenge
        };
      }).catch(function (verifyErr) {
        // Verify threw — submit succeeded but we couldn't verify. Treat
        // as ok with a warning so the user isn't blocked by a verification
        // hiccup. Telemetry would log the verifyErr.
        _recordAddManifest(actualPayload, 'verify_error');
        return {
          ok: true,
          addedItems: submitResult.addedItems,
          pattern: submitResult.pattern,
          verifyError: (verifyErr && verifyErr.message) || 'unknown'
        };
      });
    });
  }

  // -------------------------------------------------------------------
  // Doc 10 P1 — parent provenance block (_jrs_provenance)
  // -------------------------------------------------------------------

  // The per-position source map from the most recent buildProductConfiguration.
  function getLastVariantSources() { return _lastVariantSources; }

  // Assemble the compact provenance object for the parent line. Call AFTER
  // buildProductConfiguration (so getLastVariantSources() is populated). The
  // confirm handler sets config.provenance = buildProvenance(state, ctx) before
  // submitAndVerify; the payload builder stamps it as _jrs_provenance on the
  // PARENT only (no child / _bundle_raw change → heal byte-equivalence intact).
  function buildProvenance(state, context) {
    var draft = (state && state.draft) || {};
    var editing = !!(state && state.editMode && state.editMode.active);
    var isBundle = (state && state.flow === 'bundle') || draft.flow === 'bundle' ||
                   !!draft._bundle_meta || draft.branding_strategy === 'bundle_wide' ||
                   draft.branding_strategy === 'per_product';
    var out = {
      b: 'v2',                                                   // builder
      flow: editing ? 'cart_edit' : (isBundle ? (draft.branding_strategy || 'bundle') : 'multistep'),
      origin: editing ? 'cart_edit' : 'fresh',
      vs: _lastVariantSources,                                   // W3 — per-position source
      sid: _readPopupSessionId(),                                // W8 — GA4 correlation key
      dev: _deviceClass(),
      build: _readBuildId(),                                     // W7 — theme build id
      ts: _mintTimestamp()
    };
    // W1 — logoId → client-side content hash, when present. Lets fulfilment prove
    // the stored file matches the bytes the customer uploaded. Omitted when empty.
    var shas = _collectLogoShas(draft);
    if (shas && _objectKeyCount(shas) > 0) { out.logo_sha = shas; }
    return out;
  }

  function _collectLogoShas(draft) {
    var out = {};
    var assignments = (draft && draft.logo_assignments) || {};
    for (var pid in assignments) {
      if (Object.prototype.hasOwnProperty.call(assignments, pid)) {
        var a = assignments[pid];
        if (a && a.logoId && a.sha) { out[a.logoId] = a.sha; }
      }
    }
    return out;
  }

  function _readPopupSessionId() {
    try {
      if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.getSessionId === 'function') {
        return String(JRS.CustomizerPopup.getSessionId() || '');
      }
      if (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT && window.JRS_PDP_CONTEXT.popup_session_id) {
        return String(window.JRS_PDP_CONTEXT.popup_session_id);
      }
    } catch (e) { /* best-effort */ }
    return '';
  }

  function _deviceClass() {
    try {
      var w = (typeof window !== 'undefined' && window.innerWidth) || 0;
      return (w && w < 768) ? 'mobile' : ((w && w < 1024) ? 'tablet' : 'desktop');
    } catch (e) { return 'desktop'; }
  }

  function _readBuildId() {
    try {
      if (typeof window !== 'undefined' && window.JRS_BUILD_ID) { return String(window.JRS_BUILD_ID); }
    } catch (e) { /* best-effort */ }
    return '';
  }

  // -------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------

  JRS.CartSubmit = {
    getLastVariantSources: getLastVariantSources,
    buildProvenance: buildProvenance,
    classifyStockFailure: classifyStockFailure,   // v8.1.4 — also a test seam
    correctBrandingAfterPartial: correctBrandingAfterPartial, // v8.1.4b — cap branding to what landed
    _describeFailedGroup: _describeFailedGroup,
    _itemFieldCount: _itemFieldCount,           // v8.2.0 test seam
    FORM_FIELD_BUDGET: FORM_FIELD_BUDGET,       // v8.2.0 test seam   // v8.1.4 test seam
    buildProductConfiguration: buildProductConfiguration,
    submit: submit,
    verifyAfterSubmit: verifyAfterSubmit,
    submitAndVerify: submitAndVerify,

    // Test seams (NOT day-to-day public API):
    _mintInstanceId: _mintInstanceId,
    _mintTimestamp: _mintTimestamp,
    _setMintFunctions: function (mintId, mintTs) {
      if (typeof mintId === 'function') _mintInstanceId = mintId;
      if (typeof mintTs === 'function') _mintTimestamp = mintTs;
    },
    _groupItemsForSubmit: _groupItemsForSubmit,
    _orderGroupsForDisplay: _orderGroupsForDisplay,
    _describePattern: _describePattern,
    _findMissingExpectedItems: _findMissingExpectedItems,
    _recordAddManifest: _recordAddManifest,
    // Phase per_product S3 — per-product forward-path seams.
    _buildCustomizationPerProduct: _buildCustomizationPerProduct,
    _buildPerProductBundleCategories: _buildPerProductBundleCategories,
    // Never store a raw handle in a cart line / order Position (humanise fallback).
    _prettyPositionName: _prettyPositionName,
    _humanizePositionId: _humanizePositionId
  };
})(window.JRS);
